<template>
  <div ref="webinarsRef" :class="rootClass" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="webinars-content">
        <div class="webinars-content-form">
          <dynamic-form ref="formRef" :form="fields?.form" @change="handleChange" />
        </div>
        <div class="webinars-content-body" v-if="paginatedList?.length > 0">
          <site-link
            v-for="item in paginatedList"
            :key="item?.id"
            :link="item.fields?.url"
            target="_blank"
          >
            <AdaptiveImage
              class="webinars-content-body-item-bg"
              :field="{ fields: item?.fields?.webinarsImage?.value }"
              :isBackground="true"
              :lazy-load="false"
              :enableScrollDetection="false"
              :show="true"
            >
              <JssRichText
                v-if="new Date() < new Date(item?.fields?.webinarsDate?.value)"
                class="webinars-content-body-item-cardTip"
                :field="item?.fields?.webinarsTag"
              />
              <JssRichText
                class="webinars-content-body-item-cardDesc"
                :field="item?.fields?.webinarsTitle"
              />
            </AdaptiveImage>
            <div
              class="webinars-content-body-item-category"
              :class="item?.fields?.webinarsCategory?.value?.phrase?.value"
            >
              {{ item?.fields?.webinarsCategory?.value?.text?.value }}
            </div>
            <div class="webinars-content-body-item-date">
              {{ formatWebinarsDateTime(item) }}
            </div>
            <div class="webinars-content-body-item-button">
              <SiteButton v-bind="fields?.button" />
            </div>
          </site-link>
        </div>
        <div class="webinars-content-nodata" v-if="showNodata">
          <JssRichText :field="fields?.noDataText" />
        </div>
        <Pagination
          :currentPage="currentPage"
          :totalPages="totalPages"
          @update:currentPage="syncRouteForPage"
          v-if="totalPages > 1"
        />
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
// 伪静态 URL 机制暂时停用
// import { useRoute, useRouter } from 'vue-router';
import utils from '@/utils';
import api from '@/api';
// import useRoutePagination from '@/hooks/useRoutePagination';

// 第一个筛选项（Product Line）的字段名，注意与 Sitecore 表单配置保持一致（含原有拼写）
const PRODUCT_LINE_FIELD_NAME = 'Prodcut Line';
// 页码后缀支持 /page1 与 /{filter}-page1 两种形式
// 伪静态 URL 暂时停用，页码后缀解析一并注释
// const PAGE_SUFFIX_REG = /(?:-|\/)page\d+$/i;

export default {
  name: 'Webinars',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    // const route = useRoute();
    // const router = useRouter();
    const webinarsRef = ref(null);
    const formRef = ref(null);
    // const { currentPage, syncRouteForPage } = useRoutePagination();
    // 伪静态 URL 停用后，页码改为组件内部状态
    const currentPage = ref(1);
    const syncRouteForPage = (page) => {
      currentPage.value = Number(page) || 1;
    };
    const productLineFilter = ref('');
    const categoryFilter = ref('');
    const webinarsList = ref([]);
    const showNodata = ref(false);
    const totalWebinarsCount = ref(0);

    // 伪静态 URL 机制暂时停用，以下 URL 解析与同步逻辑一并注释
    /*
    // 第一个筛选项的可选值，用于识别 URL 中的筛选段
    const productLineOptionValues = computed(() => {
      const items = props.fields?.form?.fields?.items ?? [];
      const fieldItem = items.find((x) => x?.fields?.fieldName?.value === PRODUCT_LINE_FIELD_NAME);
      const rawOptions = fieldItem?.fields?.options;
      if (!Array.isArray(rawOptions)) return [];
      return rawOptions
        .map((opt) => opt?.fields?.data?.value || opt?.id || '')
        .filter((v) => v && !/^all\b/i.test(v));
    });

    // 从 URL 解析基础路径与筛选段，例如：
    // /Webinars/discovery-page1 → { basePath: '/Webinars', filter: 'discovery' }
    // /Webinars/page1 → { basePath: '/Webinars', filter: '' }
    const parseFilterPath = (path) => {
      const withoutPage = path.replace(PAGE_SUFFIX_REG, '').replace(/\/+$/, '');
      const segments = withoutPage.split('/').filter(Boolean);
      let lastSegment = segments.length ? segments[segments.length - 1] : '';
      try {
        lastSegment = decodeURIComponent(lastSegment);
      } catch (e) {
        // 忽略非法编码的路径段
      }
      const matched = productLineOptionValues.value.find(
        (v) => v.toLowerCase() === lastSegment.toLowerCase()
      );
      if (matched) {
        const rest = segments.slice(0, -1).join('/');
        return { basePath: rest ? `/${rest}` : '', filter: matched };
      }
      return { basePath: withoutPage, filter: '' };
    };

    // basePath 在组件生命周期内不变（筛选/分页只改它后面的部分）
    const { basePath, filter: initialFilter } = parseFilterPath(route.path);
    productLineFilter.value = initialFilter;

    // 把第一个筛选项的值写入伪静态 URL：
    // 选中具体值 → {base}/{filter}-page1；选 all 或清空 → {base}/page1
    const syncFilterToRoute = () => {
      const filter = productLineFilter.value;
      const targetPath = filter
        ? `${basePath}/${encodeURIComponent(filter)}-page1`
        : `${basePath}/page1`;
      if (route.path === targetPath) return;
      router
        .push({
          path: targetPath,
          query: { ...route.query },
          hash: route.hash,
          // 标记为纯筛选导航，RouteHandler 据此跳过 Layout Service 重新请求
          state: { filterOnlyNavigation: true },
        })
        .catch(() => {});
    };
    */

    const itemsPerPage = computed(() => {
      const pageSize = props.fields?.pageSize?.value;
      return pageSize ? Number(pageSize) : 3;
    });

    const totalPages = computed(() => {
      return Math.max(1, Math.ceil(totalWebinarsCount.value / itemsPerPage.value));
    });

    const paginatedList = computed(() => {
      return webinarsList.value;
    });

    const handleChange = (eldName, fieldName, value) => {
      // 兼容两种调用签名：
      // 1. DynamicForm: (fieldType, fieldName, value, originValue)
      // 2. 旧 sections/dynamic-form.vue: (item, e, ...args) → args[0] 才是选中值
      let actualFieldName = fieldName;
      let actualValue = value;

      // 如果 eldName 看起来是 item 对象（旧签名），做一次映射
      if (eldName && typeof eldName === 'object' && eldName.fields?.dataName) {
        actualFieldName = eldName.fields.dataName.value;
        actualValue = arguments[3] ?? arguments[2];
      }

      // value 可能是 option 对象、option.value 字符串、null（clear）或 undefined
      const raw =
        actualValue === null || actualValue === undefined
          ? ''
          : typeof actualValue === 'object'
            ? (actualValue.value ?? '')
            : actualValue;
      const isAll = typeof raw === 'string' && raw.toLowerCase() === 'all';
      const filterValue = isAll ? '' : raw;

      let changed = false;
      // 记录修改筛选项前是否已在第一页，避免分页 watch 与直接调用重复请求
      const wasFirstPage = currentPage.value === 1;
      if (actualFieldName === PRODUCT_LINE_FIELD_NAME) {
        productLineFilter.value = filterValue;
        changed = true;
        // 伪静态 URL 暂时停用：不再写入 URL，仅回到第一页
        // syncFilterToRoute();
        currentPage.value = 1;
      } else if (actualFieldName === 'Category') {
        categoryFilter.value = filterValue;
        changed = true;
        // 伪静态 URL 暂时停用：页码改为组件内部状态
        // syncRouteForPage(1);
        currentPage.value = 1;
      }
      if (!changed) return;

      // 页码发生变化时由 watch(currentPage) 触发请求，否则直接请求
      if (wasFirstPage) {
        getWebinars();
      }
    };

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'webinars'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const formatWebinarsDateTime = (item) => {
      const date = item?.fields?.webinarsDate?.value;
      const time = item?.fields?.webinarsTime?.value;
      const formattedDate = utils.date.formatDateWithOrdinal(date);
      if (formattedDate && time) {
        return `${formattedDate} ${time}`;
      }
      return formattedDate || time || '';
    };

    watch(currentPage, () => {
      webinarsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      getWebinars();
    });

    /*
    // 浏览器前进/后退时，从 URL 同步第一个筛选项
    watch(
      () => route.path,
      (newPath, oldPath) => {
        const { filter } = parseFilterPath(newPath);
        if (filter === productLineFilter.value) return;
        productLineFilter.value = filter;
        formRef.value?.updateFields({ [PRODUCT_LINE_FIELD_NAME]: { value: filter || null } });
        const pageChanged =
          (newPath.match(PAGE_SUFFIX_REG)?.[0] || '') !==
          (oldPath.match(PAGE_SUFFIX_REG)?.[0] || '');
        // 页码同时变化时由 watch(currentPage) 统一拉取，避免重复请求
        if (!pageChanged) {
          getWebinars();
        }
      }
    );
    */

    const getWebinars = async () => {
      const [res, ex] = await api.webinars.getWebinars({
        page: currentPage.value,
        size: itemsPerPage.value,
        productLine: productLineFilter.value,
        category: categoryFilter.value,
        rootId: props.page?.itemId,
      });
      if (ex?.list?.length) {
        webinarsList.value = ex.list;
        totalWebinarsCount.value = ex.total || 0;
        showNodata.value = false;
      } else {
        showNodata.value = true;
      }
    };

    onMounted(() => {
      // 伪静态 URL 暂时停用：不再从 URL 回显第一个筛选项
      // if (productLineFilter.value) {
      //   formRef.value?.updateFields({
      //     [PRODUCT_LINE_FIELD_NAME]: { value: productLineFilter.value },
      //   });
      // }
      getWebinars();
    });

    return {
      webinarsRef,
      formRef,
      currentPage,
      syncRouteForPage,
      totalPages,
      paginatedList,
      showNodata,
      handleChange,
      rootClass,
      rootStyle,
      formatWebinarsDateTime,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.webinars {
  position: relative;
  width: 100%;
  padding: 40px 0;
  z-index: 2;

  &-content {
    width: 100%;

    &-form {
      margin-bottom: 24px;
    }

    &-body {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &-body-item {
      background: #fbfaf7;
      border-radius: 12px;
      padding: 16px;
      border: 1px solid #e9ecef;

      &-bg {
        width: 100%;
        aspect-ratio: 1.5;
        object-fit: cover;

        .e-background-image__content {
          padding: 16px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
      }

      &-cardTip {
        display: inline-block;
        width: fit-content;
        max-width: 100%;
        padding: 2px 4px;
        background: $blue-night;
        border-radius: 2px;
        font-size: 11px;
        color: $white;

        p {
          display: inline;
          margin: 0;
          padding: 0;
        }
      }

      &-cardDesc {
        font-size: 16px;
        font-weight: 700;
        color: $white;
        line-height: 1.2;
        margin-top: 8px;
      }

      &-category {
        font-size: 11px;
        margin-top: 10px;
        padding: 2px 4px;
        line-height: 1.2;
        display: inline-block;

        &.forum {
          color: $blue-night;
          background: $grey-neutral;
        }

        &.conference {
          color: $grey-black;
          background: $neutral-disable-surface;
        }

        &.symposium {
          color: #e9550d;
          background: rgba(239, 85, 10, 0.2);
        }
      }

      &-desc {
        font-size: 14px;
        color: $grey-black;
        line-height: 1.4;
        margin-top: 10px;
        @include line-clamp(2);
      }

      &-date {
        font-size: 12px;
        color: #787878;
        line-height: 1.4;
        margin-top: 10px;
      }

      &-button {
        margin-top: 16px;
      }
    }

    &-nodata {
      padding: 32px;
      color: #3e3e3e;
      background: #f7f8f7;
      text-align: center;
      font-size: 20px;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;
    &-content {
      &-form {
        margin-bottom: 24px;

        .c-dynamic-form {
          align-items: flex-end;
        }

        .c-dynamic-form__items {
          flex-direction: row;
          gap: 24px;
        }

        .f-dropdown {
          flex-direction: row;
          gap: 16px;

          .f-dropdown__main {
            width: 230px;
          }
        }
      }

      &-body {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
      }

      &-body-item {
        background: #fbfaf7;
        border-radius: 16px;
        padding: 24px;
        border: 1px solid #e9ecef;

        &-bg {
          width: 100%;
          aspect-ratio: 1.5;
          object-fit: cover;
          .e-background-image__content {
            padding: 24px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
        }

        &-cardTip {
          display: inline-block;
          width: fit-content;
          max-width: 100%;
          padding: 3px 6px;
          background: $blue-night;
          border-radius: 2px;
          font-size: 12px;
          color: $white;

          p {
            display: inline;
            margin: 0;
            padding: 0;
          }
        }

        &-cardDesc {
          font-size: 18px;
          font-weight: 700;
          color: $white;
          line-height: 1.2;
          margin-top: 12px;
        }

        &-category {
          font-size: 12px;
          margin-top: 12px;
          padding: 3px 6px;
          line-height: 1.2;
          display: inline-block;

          &.forum {
            color: $blue-night;
            background: $grey-neutral;
          }

          &.conference {
            color: $grey-black;
            background: $neutral-disable-surface;
          }

          &.symposium {
            color: #e9550d;
            background: rgba(239, 85, 10, 0.2);
          }
        }

        &-desc {
          font-size: 16px;
          font-weight: 700;
          color: $grey-black;
          line-height: 1.4;
          margin-top: 12px;
        }

        &-date {
          font-size: 14px;
          color: #787878;
          line-height: 1.4;
          margin-top: 12px;
        }

        &-button {
          margin-top: 25px;
        }
      }
    }
  }
}
</style>
